import { useRef, useState } from "react";
import { Flex, Button, Text, useToast, Box, useBreakpointValue } from "@chakra-ui/react";
import { Formik, Form } from "formik";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import ensureAuthenticated from "@/contexts/ensureAuthenticated";
import { axiosInstance } from "@/services/axiosInstance";
import { usernameFormatter } from "@/utils/usernameFormatter";
import { UsernameSchema, PasswordSchema } from "@/utils/loginFormValidation";
import LoginInput2 from "../LoginInput/index2";
import Logo from "../Logo";
import { AxiosError } from "axios";

export default function LoginForm2() {
  const [visiblePassword, setVisiblePassword] = useState(false);
  const [step, setStep] = useState(1);
  const { setAuthenticated } = ensureAuthenticated();
  const toast = useToast();
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const isMobile = useBreakpointValue({ base: true, md: false });

  return (
    <Flex
      w="100%"
      h={isMobile ? "50%" : "100%"}
      justify="center"
      bgColor="transparent"
      flexDir="column"
      color="white"
      fontFamily="Lexend Deca"
      ml={isMobile ? "5%" : "10%"}
      mt={isMobile ? "15%" : "0%"}
    >
      <Flex
        mb="3rem"
        align="flex-start"
        justify="flex-start"
        flexDir="column"
        w={isMobile ? "100%" : "50%"}
        h="auto"
      >
        {!isMobile && (
          <Logo
            variant="primary"
            alt="Logo"
            mb="4"
            boxSize="30%"
            objectFit="contain"
            mx="0"
            my="3"
          />
        )}
        <Text
          fontSize={isMobile ? "3xl" : "6xl"}
          textShadow="0 0 5px rgba(0,0,0,0.5)"
          lineHeight="1"
          mb="-2"
          mt={{ base: "20", md: "0" }}
        >
          Área Do
        </Text>
        <Text
          fontSize={isMobile ? "4xl" : "7xl"}
          textShadow="0 0 5px rgba(0,0,0,0.5)"
          lineHeight="1"
          mt={2}
        >
          Administrador
        </Text>
      </Flex>
      <Formik
        initialValues={{
          username: "",
          password: "",
        }}
        validationSchema={step === 1 ? UsernameSchema : PasswordSchema}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          if (step === 1) {
            setStep(2);
            setSubmitting(false);
          } else {
            try {
              const { data, status } = await axiosInstance.post("api/auth/signin", {
                id: values.username,
                password: values.password,
              });
              if (status === 200) {
                setAuthenticated({ isAuthenticated: true, user: data.user });
                localStorage.setItem("session", data.session);
                localStorage.setItem("refreshToken", data.refreshToken);
              }
            } catch (err) {
              const error = err as AxiosError;

              if (error.response?.status === 401) {
                setStep(1);
                resetForm();
                toast({
                  title: "Usuário ou senha incorretos",
                  description: "Por favor, verifique suas credenciais e tente novamente.",
                  status: "error",
                  duration: 3000,
                  isClosable: true,
                });
              } else if (error.response?.status === 403) {
                setStep(1);
                resetForm();
                toast({
                  title: "Erro de permissão",
                  description: "Usuário não é administrador",
                  status: "error",
                  duration: 5000,
                  isClosable: true,
                });
              } else {
                toast({
                  title: "Erro ao Efetuar Login",
                  description: "Ocorreu um erro inesperado. Tente novamente mais tarde.",
                  status: "error",
                  duration: 3000,
                  isClosable: true,
                });
              }
            } finally {
              setSubmitting(false);
            }
          }
        }}
      >
        {({ errors, touched, setFieldValue, values, isSubmitting }) => (
          <Form
            style={{
              width: "100%",
              flexDirection: "column",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
            }}
          >
            <Box
              w={{ base: "100%", md: "90%" }}
              ml={{ base: "0", md: "0" }}
              pr="5%"
              mt={{ base: 10, md: 0 }}
            >
              {step === 1 && (
                <>
                  <LoginInput2
                    name="username"
                    touched={touched}
                    id="username"
                    title="username"
                    placeholder="Digite Aqui Seu Login"
                    setFieldValue={setFieldValue}
                    value={values.username}
                    formatFn={usernameFormatter}
                    fontSize={isMobile ? "2xl" : "4xl"}
                    w="100%"
                    maxWidth={{ base: "100%", md: "900px" }}
                    variant="flushed"
                    textShadow="0 0 5px rgba(0,0,0,0.5)"
                    h="60px"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (!errors.username) {
                          setStep(2);
                          passwordInputRef.current?.focus();
                        }
                      }
                    }}
                  />
                  {errors.username && touched.username ? (
                    <Text color="red">{errors.username}</Text>
                  ) : null}
                </>
              )}
              {step === 2 && (
                <>
                  <LoginInput2
                    name="password"
                    touched={touched}
                    id="password"
                    type={visiblePassword ? "text" : "password"}
                    title="password"
                    placeholder="Senha"
                    isPassword={true}
                    rightIcon={
                      visiblePassword ? (
                        <EyeInvisibleOutlined style={{ color: "#CACDD1" }} />
                      ) : (
                        <EyeOutlined style={{ color: "#CACDD1" }} />
                      )
                    }
                    onIconClick={() => setVisiblePassword(!visiblePassword)}
                    setFieldValue={setFieldValue}
                    fontSize={isMobile ? "2xl" : "4xl"}
                    w="100%"
                    maxWidth={{ base: "100%", md: "900px" }}
                    variant="flushed"
                    textShadow="0 0 5px rgba(0,0,0,0.5)"
                    h="70px"
                    autoComplete="current-password"
                    autoFocus={step === 2}
                  />
                  {errors.password && touched.password ? (
                    <Text color="red">{errors.password}</Text>
                  ) : null}
                </>
              )}
              <Button
                bgColor="white"
                w="100%"
                color="#85c773"
                borderRadius={isMobile ? "24px" : "48px"}
                type="submit"
                isLoading={isSubmitting}
                mt={isMobile ? "2" : "4"}
                my={isMobile ? "2" : "4"}
                fontSize={isMobile ? "2xl" : "4xl"}
                h="80px"
              >
                Entrar
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Flex>
  );
}
